import { cn, formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

/**
 * Online price cell — the live price, plus the struck-through regular price and
 * a discount badge when the variant is on sale. Shared by the Products and
 * Variants lists so an on-sale item reads identically on both.
 */
export function OnlinePrice({
  price,
  compareAtPrice,
  isOnSale,
}: {
  price: number | null;
  compareAtPrice: number | null;
  isOnSale: boolean;
}) {
  if (!isOnSale || price == null || compareAtPrice == null) {
    return <span className="text-sm font-mono text-foreground">{formatCurrency(price)}</span>;
  }
  const pct = Math.round((1 - price / compareAtPrice) * 100);
  return (
    <div
      className="flex flex-wrap items-baseline gap-1.5"
      title="On sale — regular price struck through"
    >
      <span className="text-sm font-mono text-foreground">{formatCurrency(price)}</span>
      <span className="text-xs font-mono text-muted-foreground line-through">
        {formatCurrency(compareAtPrice)}
      </span>
      <Badge variant="success" className="px-1 py-0 text-[9px]">
        −{pct}%
      </Badge>
    </div>
  );
}

/** Dollar difference between the nursery and online price, signed and colored. */
export function PriceDiff({
  online,
  nursery,
}: {
  online: number | null;
  nursery: number | null;
}) {
  if (online == null || nursery == null) {
    return <span className="text-xs text-muted-foreground">{'—'}</span>;
  }
  const diff = nursery - online;
  const pct = online !== 0 ? Math.round((diff / online) * 100) : 0;
  return (
    <span
      className={cn(
        'text-xs font-mono',
        diff < 0 ? 'text-success' : diff > 0 ? 'text-destructive' : 'text-muted-foreground',
      )}
      title={`${diff >= 0 ? '+' : ''}${pct}% vs online`}
    >
      {diff > 0 ? '+' : diff < 0 ? '-' : ''}
      {formatCurrency(Math.abs(diff))}
    </span>
  );
}
